import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BASE_URL from "../../BASEURL";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPen, faTrash } from "@fortawesome/free-solid-svg-icons";

export default function PersonList() {
  const [persons, setPersons] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [updating, setUpdating] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [personsRes, designationsRes] = await Promise.all([
          fetch(`${BASE_URL}/api/persons`),
          fetch(`${BASE_URL}/api/designations`),
        ]);

        if (!personsRes.ok || !designationsRes.ok) {
          throw new Error("Failed to load data");
        }

        const personsData = await personsRes.json();
        const designationsData = await designationsRes.json();

        setPersons(personsData);
        setDesignations(designationsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = (id) => {
    setConfirmId(id);
    setShowConfirm(true);
  };

  const handleEdit = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/persons/${id}`);
      if (!res.ok) throw new Error("Failed to load person");
      const data = await res.json();

      setEditingPerson(data);
      setShowEditModal(true);
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    const formData = new FormData();
    formData.append("person_name", editingPerson.person_name);
    formData.append("person_designation", editingPerson.person_designation?._id || editingPerson.person_designation);
    formData.append("person_role", editingPerson.person_role);
    formData.append("person_mobile", editingPerson.person_mobile);
    formData.append("person_batch", editingPerson.person_batch);

    if (editingPerson.person_photo && typeof editingPerson.person_photo !== "string") {
      formData.append("person_photo", editingPerson.person_photo);
    }

    try {
      const res = await fetch(`${BASE_URL}/api/persons/${editingPerson._id}`, {
        method: "PUT",
        body: formData,
      });

      if (!res.ok) throw new Error("Update failed");

      setPersons((prev) =>
        prev.map((p) => (p._id === editingPerson._id ? { ...p, ...editingPerson } : p))
      );

      setShowEditModal(false);
      setEditingPerson(null);
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    } finally {
      setUpdating(false);
    }
  };

  const confirmDelete = async () => {
    setShowConfirm(false);
    try {
      const res = await fetch(`${BASE_URL}/api/persons/${confirmId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");

      setPersons((prev) => prev.filter((p) => p._id !== confirmId));
    } catch (err) {
      setErrorMessage(err.message);
      setShowError(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold text-xl font-medium">Loading persons...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl font-medium">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className=" bg-gray-50 px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Person List</h1>

        <Link
          to="/admin/add-person"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-gray-900 font-semibold rounded-lg shadow-md hover:bg-amber-600 hover:text-white transition-all duration-200"
        >
          <FontAwesomeIcon icon={faPlus} className="text-sm" />
          Add New Person
        </Link>
      </div>

      {/* Desktop Table - lg and up */}
      <div className="hidden lg:block bg-white rounded-xl shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Photo</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Designation</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Role</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Mobile Number</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white">Batch</th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {persons.map((person) => (
              <tr key={person._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <img
                    src={person.person_photo}
                    alt={person.person_name}
                    className="h-12 w-12 rounded-full object-cover border border-gray-200"
                  />
                </td>
                <td className="px-6 py-4 font-medium text-gray-900">{person.person_name}</td>
                <td className="px-6 py-4 text-gray-600">
                  {person.person_designation?.designation_name || person.person_designation}
                </td>
                <td className="px-6 py-4 text-gray-600">{person.person_role}</td>
                <td className="px-6 py-4 text-gray-600">{person.person_mobile}</td>
                <td className="px-6 py-4 text-gray-600">{person.person_batch}</td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleEdit(person._id)}
                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      onClick={() => handleDelete(person._id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile + Tablet Cards */}
      <div className="lg:hidden grid gap-5 sm:grid-cols-2">
        {persons.map((person) => (
          <div
            key={person._id}
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-200"
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-4">
                <img
                  src={person.person_photo}
                  alt={person.person_name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{person.person_name}</h3>
                  <p className="text-sm text-gray-600 truncate">
                    {person.person_designation?.designation_name || person.person_designation || "—"}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Role:</span>
                  <p className="text-gray-600">{person.person_role || "—"}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Mobile Number:</span>
                  <p className="text-gray-600">{person.person_mobile || "—"}</p>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Batch:</span>
                  <p className="text-gray-600">{person.person_batch || "—"}</p>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => handleEdit(person._id)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition"
                >
                  <FontAwesomeIcon icon={faPen} className="text-sm" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(person._id)}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Person</h2>

              <form onSubmit={handleUpdate} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editingPerson.person_name}
                    onChange={(e) => setEditingPerson({ ...editingPerson, person_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <select
                    value={editingPerson.person_designation?._id || editingPerson.person_designation || ""}
                    onChange={(e) => setEditingPerson({ ...editingPerson, person_designation: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  >
                    <option value="">Select Designation</option>
                    {designations.map((d) => (
                      <option key={d._id} value={d._id}>
                        {d.designation_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Other fields – same pattern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <input
                    type="text"
                    value={editingPerson.person_role}
                    onChange={(e) => setEditingPerson({ ...editingPerson, person_role: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input
                    type="tel"
                    maxLength="10"
                    value={editingPerson.person_mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      setEditingPerson({ ...editingPerson, person_mobile: value });
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                  <input
                    type="text"
                    value={editingPerson.person_batch}
                    onChange={(e) => setEditingPerson({ ...editingPerson, person_batch: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditingPerson({ ...editingPerson, person_photo: e.target.files[0] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 py-3 px-6 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="flex-1 py-3 px-6 bg-amber-500 text-gray-900 rounded-lg hover:bg-amber-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? "Updating..." : "Update Person"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this person? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h2 className="text-xl font-semibold text-red-700 mb-3">Error</h2>
            <p className="text-gray-700 mb-6">{errorMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowError(false)}
                className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}